import type { CSSProperties } from "react"
import { waitFor } from "xstate"
import { useSelector } from "@xstate/react"
import { Fragment, useEffect, useMemo } from "react"
import { isRouteErrorResponse, Link, Outlet, redirect, useNavigate, useNavigation, useRouteError } from "react-router"
import { Toaster, toast } from "sonner"

import { getRedirectPath } from "@recordsure/unity-auth-machine"

import { Button } from "@/components/ui/button"
import { InfoCard } from "@/components/infoCard"

import { authMachine, hasPermission } from "@/machines/Authmachine"
import { loadProjects, getProjects, getOrganisations } from "@/utils/projects"

import type { APIError } from "@/utils"

import Breadcrumbs from "./breadcrumb"

async function loader() {
  await waitFor(authMachine, (snapshot) => snapshot.hasTag("Settled"))

  const snapshot = authMachine.getSnapshot()

  if (snapshot.hasTag("AuthIncomplete")) {
    const redirectPath = getRedirectPath()
    return redirect(`/auth${redirectPath === "/" ? "" : `?from=${redirectPath}`}`)
  }

  if (hasPermission("readAccess")) {
    await loadProjects()
    return { organisations: getOrganisations(), projects: getProjects() }
  }

  throw new Response("Unauthorized", { status: 401 })
}

export function Root() {
  const navigate = useNavigate()
  const navigation = useNavigation()

  const isAuthErrored = useSelector(authMachine, (snapshot) => snapshot.matches("error"))

  const AUTH_ERROR_TOAST_ID = "auth-error"

  const isNormalLoad = useMemo(
    () => navigation.state === "loading" && navigation.formData == null,
    [navigation.formData, navigation.state],
  )

  useEffect(() => {
    const subscription = authMachine.on("UNAUTHENTICATED", () => void navigate("/auth"))
    return () => subscription.unsubscribe()
  }, [navigate])

  useEffect(() => {
    if (!isAuthErrored) return

    const id = toast.warning("Authentication Error", {
      id: AUTH_ERROR_TOAST_ID,
      description:
        "Error refreshing account session. Use the button to manually retry the process and prevent data loss.",
      duration: Infinity,
      className: "border-warning bg-warning/10 text-warning-content",
      classNames: {
        actionButton:
          "!bg-transparent !border !border-warning !text-warning-content hover:!bg-warning hover:!text-warning-foreground",
      },
      action: {
        label: "Log In",
        onClick: () => authMachine.send({ type: "EXTERNAL_LOGIN" }),
      },
    })

    return () => {
      toast.dismiss(id)
    }
  }, [isAuthErrored])

  return (
    <>
      <div className="flex h-screen w-full flex-col items-start justify-start divide-y divide-black/20 overflow-x-hidden bg-background">
        <nav className="HeaderZIndex sticky top-0 flex w-full flex-col items-center justify-between gap-4 px-4 py-2 sm:flex-row">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="Home Page" className="block shrink-0">
              <img
                aria-hidden
                loading="lazy"
                alt="Recordsure Logo"
                src="/logo.png"
                className="w-full max-w-[2.25rem]"
              />
            </Link>
            <Breadcrumbs />
          </div>
        </nav>
        <main className="h-screen max-h-screen w-full overflow-hidden p-4">
          <Outlet />
        </main>
      </div>
      <InfoCard />
      <Toaster
        theme="light"
        position="bottom-right"
        richColors
        style={
          {
            "--normal-bg": "var(--background)",
            "--normal-text": "var(--foreground)",
            "--normal-border": "var(--border)",
          } as CSSProperties
        }
      />
      {isNormalLoad && <progress className="fixed top-0 z-10000000000000 h-1 w-full page-progress" />}
    </>
  )
}

function ErrorBoundary() {
  const error = useRouteError() as Response

  useEffect(() => {
    console.error(error)
  }, [error])

  return (
    <div className="flex h-full w-full flex-1 flex-col items-center justify-center gap-7 p-4">
      <div className="max-w-md space-y-4 text-center">
        {isRouteErrorResponse(error) ? (
          <Fragment>
            <h1 className="text-7xl font-bold text-foreground">{error.status}</h1>
            {error.status === 400 ? (
              <p className="text-sm">{(error.data as APIError[]).map((e) => e.errorMessage).join(" ")}</p>
            ) : error.status === 401 ? (
              <h2 className="text-xl font-bold">
                Sorry, we&apos;ll need you to re-login.
                <Button type="button" variant="outline" onClick={() => authMachine.send({ type: "LOGIN", path: null })}>
                  Click Here to login
                </Button>
              </h2>
            ) : error.status === 403 ? (
              <Fragment>
                <h2 className="text-xl font-bold">Sorry, you do not have the permissions to perform this request</h2>
                <p>
                  If you feel you have reached this page in error, please speak to your IT support personnel to get help
                  with this issue.
                </p>
              </Fragment>
            ) : (
              <h2 className="text-center text-xl font-bold text-foreground">
                {typeof error.data === "string"
                  ? error.data
                  : typeof error.data === "object" && "message" in error.data
                    ? (error.data as { message: string }).message
                    : "Something went wrong!!"}
              </h2>
            )}
          </Fragment>
        ) : (
          <h2 className="text-center text-3xl font-bold text-foreground">Something went wrong!!</h2>
        )}
      </div>
    </div>
  )
}

Root.routeId = "root"
Root.loader = loader
Root.ErrorBoundary = ErrorBoundary

export type RootLoader = typeof loader
